import type { VtProjectKind, VtRgaLevel } from "../../../../enums.js";
import {
  VT_FOUNDATION_TYPES,
  type VtFoundationType,
  type VtGroundType,
  type VtSlopeCategory,
} from "../sections/terrain.js";

export const FOUNDATION_MATRIX_VERSION = "2026-07-v2";

const PLATINES: VtFoundationType[] = ["platines_pieux", "platines_goujons"];
const WEASYFIX_BASE: VtFoundationType[] = ["weasyfix_75", "weasyfix_120"];
// Matrice PDF §3 : solution lourde = choix entre A (technopieux) et
// B (vide sanitaire maçonné). `vide_sanitaire_semelle_filante` reste dans
// l'enum pour les payloads historiques mais n'est plus proposé en lourde.
const HEAVY: VtFoundationType[] = [
  "technopieux",
  "vide_sanitaire_maconnerie_plancher_bois",
];
const SANDY: VtGroundType[] = ["sable", "terre_sablonneuse"];
const ALL: VtFoundationType[] = [...VT_FOUNDATION_TYPES];

export type FoundationMatrixInput = {
  projectKind?: VtProjectKind;
  surfaceM2?: number;
  rgaLevel?: VtRgaLevel;
  slopeCategory?: VtSlopeCategory;
  groundType?: VtGroundType;
};

export type FoundationMatrixResult = {
  mode: "restricted" | "manual";
  allowed: VtFoundationType[];
  requiresSoilStudy: boolean;
  note?: string;
};

function manual(note?: string): FoundationMatrixResult {
  return { mode: "manual", allowed: ALL, requiresSoilStudy: false, note };
}

function heavy(note: string): FoundationMatrixResult {
  return { mode: "restricted", allowed: HEAVY, requiresSoilStudy: true, note };
}

function weasyfixFor(ground?: VtGroundType): VtFoundationType[] {
  return ground && SANDY.includes(ground)
    ? [...WEASYFIX_BASE, "weasyfix_sable"]
    : WEASYFIX_BASE;
}

function slopeBand(
  s?: VtSlopeCategory,
): "flat" | "light" | "steep" | "unknown" {
  if (s === "nulle") return "flat";
  if (s === "legere") return "light";
  if (s === "moyenne" || s === "forte" || s === "tres_forte") return "steep";
  return "unknown";
}

export function foundationMatrix(
  input: FoundationMatrixInput = {},
): FoundationMatrixResult {
  const { projectKind, surfaceM2, rgaLevel, slopeCategory, groundType } = input;

  if (!projectKind || surfaceM2 === undefined || surfaceM2 <= 0) {
    return manual("Contexte projet incomplet — choix libre");
  }
  if (projectKind === "gx") return manual("GX — arbitrage cas par cas");
  if (projectKind === "ccmi") {
    return heavy("CCMI — confirmer solution A/B selon scan contrat");
  }

  const band = surfaceM2 < 50 ? "lt50" : surfaceM2 > 50 ? "gt50" : "eq50";
  if (band === "gt50") return heavy("GK > 50 m² — solution lourde");
  if (band === "lt50" && rgaLevel === "eleve") {
    return heavy("GK < 50 m² · RGA élevé — solution lourde");
  }
  if (band === "eq50") {
    if (!rgaLevel) return manual("RGA requis pour trancher GK 50 m²");
    if (rgaLevel === "modere" || rgaLevel === "eleve") {
      return heavy(
        `GK 50 m² · RGA ${rgaLevel === "modere" ? "modéré" : "élevé"} — solution lourde`,
      );
    }
  }

  switch (slopeBand(slopeCategory)) {
    case "flat":
      return {
        mode: "restricted",
        allowed: PLATINES,
        requiresSoilStudy: false,
        note: "Terrain sans pente — platines",
      };
    case "light":
      return {
        mode: "restricted",
        allowed: [...PLATINES, ...weasyfixFor(groundType)],
        requiresSoilStudy: false,
        note: "Pente légère — platines ou weasyfix",
      };
    case "steep":
      if (groundType === "terre_vegetale") {
        return {
          mode: "restricted",
          allowed: weasyfixFor(groundType),
          requiresSoilStudy: false,
          note: "Pente + terre végétale — weasyfix",
        };
      }
      return manual("Pente forte + support non-végétal — règle non définie");
    default:
      return manual("Pente non renseignée — choix libre en attendant");
  }
}
